import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/Layout';
import { Loader } from './components/ui';

import Login from './pages/Login';
import Landing from './site/Landing';
import Privacy from './site/Privacy';
import DeleteAccount from './site/DeleteAccount';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import Users from './pages/Users';
import Authorizations from './pages/Authorizations';
import Categories from './pages/Categories';
import Reasons from './pages/Reasons';
import Units from './pages/Units';
import Materials from './pages/Materials';
import Memberships from './pages/Memberships';
import Approvals from './pages/Approvals';
import Cancellations from './pages/Cancellations';
import Tickets from './pages/Tickets';
import Brands from './pages/Brands';
import Products from './pages/Products';
import Inventory from './pages/Inventory';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Contracts from './pages/Contracts';
import Payments from './pages/Payments';
import Shipments from './pages/Shipments';
import Claims from './pages/Claims';
import Insurance from './pages/Insurance';
import Finance from './pages/Finance';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader label="Restoring your session…" />;
  if (!user) return <Navigate to="/admin/loginadmin" replace />;
  return children;
};

const Routing = () => (
  <Routes>
    {/* Public website. The console lives under /admin so the root can be the
        marketing site, and Google Play can reach the policy and deletion
        pages without anyone installing the app. */}
    <Route path="/" element={<Landing />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
    <Route path="/delete-account" element={<DeleteAccount />} />

    <Route path="/admin/loginadmin" element={<Login />} />
    {/* The old login path, kept so existing bookmarks still land somewhere. */}
    <Route path="/login" element={<Navigate to="/admin/loginadmin" replace />} />

    <Route
      path="/admin"
      element={
        <Protected>
          <Layout />
        </Protected>
      }
    >
      <Route index element={<Dashboard />} />
      <Route path="companies" element={<Companies />} />
      <Route path="users" element={<Users />} />
      <Route path="authorizations" element={<Authorizations />} />
      <Route path="categories" element={<Categories />} />
      <Route path="brands" element={<Brands />} />
      <Route path="products" element={<Products />} />
      <Route path="inventory" element={<Inventory />} />
      <Route path="orders" element={<Orders />} />
      <Route path="orders/:id" element={<OrderDetail />} />
      <Route path="contracts" element={<Contracts />} />
      <Route path="payments" element={<Payments />} />
      <Route path="shipments" element={<Shipments />} />
      <Route path="approvals" element={<Approvals />} />
      <Route path="units" element={<Units />} />
      <Route path="materials" element={<Materials />} />
      <Route path="memberships" element={<Memberships />} />
      <Route path="cancellations" element={<Cancellations />} />
      <Route path="reasons" element={<Reasons />} />
      <Route path="tickets" element={<Tickets />} />
      <Route path="claims" element={<Claims />} />
      <Route path="insurance" element={<Insurance />} />
      <Route path="finance" element={<Finance />} />
      <Route path="reports" element={<Reports />} />
      <Route path="notifications" element={<Notifications />} />
      <Route path="settings" element={<Settings />} />
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <ToastProvider>
    <AuthProvider>
      <Routing />
    </AuthProvider>
  </ToastProvider>
);

export default App;
